/**
 * Hệ thống phân quyền theo permission keys (dạng "module.action").
 *
 * - `data.type === 1` ⇒ Super Admin: toàn quyền (không cần check permissions).
 * - Các tài khoản khác: quyền được lấy từ `data.info.role.permissions` khi đăng nhập.
 *
 * Mỗi route được map tới một permission key. Khi user truy cập route,
 * hàm `canAccess` kiểm tra:
 *   1. Nếu super admin ⇒ cho phép.
 *   2. Nếu route không yêu cầu permission (empty string) ⇒ cho phép.
 *   3. Nếu permissions chứa key yêu cầu ⇒ cho phép.
 *   4. Ngược lại ⇒ từ chối.
 *
 * Để thêm route mới: thêm vào `ROUTE_PERMISSIONS` với permission key tương ứng.
 * Dùng prefix kết thúc bằng '/' để match các route con (vd '/customer/' khớp '/customer/:id').
 */

export const ROUTE_PERMISSIONS: Record<string, string> = {
  // Public-protected (đã login là vào được)
  '/unauthorized': '',
  // Trang thử nghiệm Supabase Realtime: đã login là vào được, nhưng KHÔNG
  // dùng làm route mặc định sau khi đăng nhập (xem getDefaultRoute).
  '/realtime-demo': '',
  // Thông báo là dữ liệu cá nhân của người đăng nhập -> không cần quyền riêng
  '/notifications': '',

  // Tổng quan
  '/': 'overview.view',

  // CMS / nghiệp vụ
  '/chats': 'chats.view',
  '/chat/': 'chats.view',
  '/customer': 'customer.view',
  '/customer/': 'customer.view',
  '/appointments': 'appointment.view',
  '/appointment/create': 'appointment.create',
  '/appointment/detail/': 'appointment.view',
  '/appointment/': 'appointment.view',
  '/consultations': 'consultation.view',
  '/consultation/detail/': 'consultation.view',
  '/execution-schedule': 'execution.view',
  '/execution-schedule/detail/': 'execution.view',
  '/list-order': 'list_order.view',
  '/order-detail': 'list_order.view',

  // Chuyên môn
  '/hair-clinic': 'hair_clinic.view',
  '/miko-reception': 'miko_reception.view',
  '/cosmetic-surgery-reception': 'cosmetic_surgery.view',
  '/thread-lift-reception': 'thread_lift.view',
  '/consultation-form': 'ozempic.view',
  '/beauty-consultation': 'beauty_consultation.view',
  '/consultation-tabs': 'skin_consultation.view',

  // Danh mục
  '/customer-tags': 'sale_tags.view',
  // Ma trận chuyển trạng thái cấu hình trên chính các thẻ sale -> dùng chung quyền
  '/appointment-transitions': 'sale_tags.view',
  '/care-status': 'chat_tags.view',
  '/profession': 'profession.view',
  '/quick-messages': 'quick_messages.view',

  // Nhân viên
  '/employee': 'employee.view',
  '/group-roles': 'group_roles.view',
  '/departments': 'departments.view',

  // Dịch vụ & Sản phẩm
  '/services': 'service_categories.view',
  '/services-list': 'services.view',
  '/products-cat': 'product_categories.view',
  '/products': 'products.view',
  '/product/add': 'products.create',
  '/warehouses': 'products.view',

  // Khác
  '/advertise': 'advertise.view',
  '/advertise/detail': 'advertise.view',
  '/branch-manager': 'branch_manager.view',

  // Báo cáo
  '/overview-report': 'overview_report.view',
  '/page-report': 'page_report.view',
  '/sale-online-report': 'sale_online_report.view',
  '/customer-report': 'customer_report.view',
  '/business-report': 'overview_report.view',
};

/**
 * Lấy permission key yêu cầu cho 1 pathname (longest prefix match).
 * Trả về `null` nếu route không nằm trong bảng (mặc định: từ chối).
 */
export function getRequiredPermission(pathname: string): string | null {
  let best: { prefix: string; perm: string } | null = null;
  for (const [prefix, perm] of Object.entries(ROUTE_PERMISSIONS)) {
    const matched = prefix.endsWith('/')
      ? pathname.startsWith(prefix)
      : pathname === prefix;
    if (matched && (!best || prefix.length > best.prefix.length)) {
      best = { prefix, perm };
    }
  }
  return best ? best.perm : null;
}

/**
 * Kiểm tra quyền truy cập 1 pathname.
 */
export function canAccess(
  pathname: string,
  permissions: string[] = [],
  isSuperAdmin = false
): boolean {
  if (isSuperAdmin) return true;
  const required = getRequiredPermission(pathname);
  if (required === null) return false;
  if (required === '') return true;
  return permissions.includes(required);
}

/**
 * Route mặc định sau khi đăng nhập. Super admin về '/', còn lại vào
 * route đầu tiên trong `ROUTE_PERMISSIONS` mà user có quyền.
 */
export function getDefaultRoute(
  permissions: string[] = [],
  isSuperAdmin = false
): string {
  if (isSuperAdmin) return '/';
  // Không ưu tiên chuyển hướng tới các trang thử nghiệm/không phải nghiệp vụ
  const EXCLUDED_DEFAULT_ROUTES = ['/unauthorized', '/realtime-demo', '/notifications'];
  const candidates = Object.keys(ROUTE_PERMISSIONS).filter(
    (p) => !EXCLUDED_DEFAULT_ROUTES.includes(p) && !p.endsWith('/')
  );
  for (const route of candidates) {
    if (canAccess(route, permissions, isSuperAdmin)) return route;
  }
  return '/unauthorized';
}

/** Helper: kiểm tra user có 1 permission key cụ thể không. */
export function hasPermission(
  permissions: string[],
  key: string,
  isSuperAdmin = false
): boolean {
  if (isSuperAdmin) return true;
  if (!key) return true;
  return permissions.includes(key);
}

